import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Grid,
  GridItem,
  Select,
  InputGroup,
  InputLeftElement,
  Input,
  Icon,
  Button,
  HStack,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  MdSearch,
  MdDescription,
  MdWarning,
  MdCheckCircle,
  MdAdd,
  MdEdit,
  MdBlock,
  MdAutorenew,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import httpClient from 'utils/httpClient';
import {
  getAllContracts,
  getExpiringContracts,
  createContract,
  updateContract,
  terminateContract,
  renewContract,
} from 'services/employee-contract.service';

const PAGE_SIZE = 15;

const EMPTY_CONTRACT_FORM = {
  employeeId: '',
  contractType: 'FIXED_TERM',
  startDate: '',
  endDate: '',
  position: '',
  department: '',
  sectionName: 'Docencia',
  categoryName: '',
  referenceName: '',
  hours: '',
  cediiapRate: '',
  cediiapPayMode: 'HOURLY',
  laudoRate: '',
  laudoPayMode: 'HOURLY',
  laudoStartDate: '',
  weeklyHours: '40',
  salary: '',
  notes: '',
};

function actorUserId() {
  try {
    const raw = localStorage.getItem('userProfile');
    const user = raw ? JSON.parse(raw) : null;
    const id = Number(user?.userId || user?.id);
    return Number.isFinite(id) && id > 0 ? id : 1;
  } catch {
    return 1;
  }
}

const money = new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' });
const payModeLabel = (mode) => (String(mode).toUpperCase() === 'MONTHLY' ? 'Mensual' : 'Por hora');

function nominalTotal(rate, hours, mode) {
  const value = Number(rate);
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (String(mode || 'HOURLY').toUpperCase() === 'MONTHLY') return value;
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return 0;
  return value * h;
}

function dayBefore(isoDate) {
  if (!isoDate) return undefined;
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

const CONTRACT_TYPE_LABEL = {
  INDEFINITE: 'Indefinido',
  FIXED_TERM: 'Plazo fijo',
  TEMPORARY: 'Temporal',
  TRIAL: 'Prueba',
  OTHER: 'Otro',
};

const CONTRACT_STATUS = {
  ACTIVE: { label: 'Activo', color: 'green' },
  EXPIRED: { label: 'Vencido', color: 'red' },
  TERMINATED: { label: 'Terminado', color: 'gray' },
  RENEWED: { label: 'Renovado', color: 'blue' },
};

const TYPE_BADGE_SCHEME = {
  INDEFINITE: 'purple',
  FIXED_TERM: 'blue',
  TEMPORARY: 'orange',
  TRIAL: 'cyan',
  OTHER: 'gray',
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('es-UY');
}

export default function EmployeeContractsView() {
  const [searchParams] = useSearchParams();
  const employeeIdFilter = searchParams.get('employeeId') || '';
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [contracts, setContracts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [employees, setEmployees] = useState([]);
  const toast = useToast();

  const newModal = useDisclosure();
  const renewModal = useDisclosure();
  const terminateDialog = useDisclosure();
  const cancelRef = useRef();

  const [newForm, setNewForm] = useState({
    ...EMPTY_CONTRACT_FORM,
    employeeId: employeeIdFilter,
  });

  const [renewForm, setRenewForm] = useState({ id: null, newEndDate: '', salaryAgreed: '' });
  const [yearForm, setYearForm] = useState(null);
  const [terminateId, setTerminateId] = useState(null);

  const loadEmployees = async () => {
    try {
      let res;
      try {
        res = await httpClient.get('/employee');
      } catch {
        res = await httpClient.get('/employees');
      }
      const list = Array.isArray(res) ? res : res?.payload ?? [];
      setEmployees(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setEmployees([]);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = statusFilter ? { status: statusFilter } : {};
      if (employeeIdFilter) filters.employeeId = employeeIdFilter;
      const [allRes, exRes] = await Promise.all([
        getAllContracts(filters),
        getExpiringContracts(30),
      ]);
      const data = allRes?.payload?.data || [];
      setContracts(Array.isArray(data) ? data : []);
      const ex = exRes?.payload?.data || [];
      setExpiring(Array.isArray(ex) ? ex : []);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudieron cargar los contratos', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter, employeeIdFilter]);

  useEffect(() => {
    if (newModal.isOpen) loadEmployees();
  }, [newModal.isOpen]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const activos = contracts.filter((c) => c.status === 'ACTIVE').length;
    const vencidos = contracts.filter((c) => c.status === 'EXPIRED').length;
    const porVencer = expiring.length;
    return { activos, vencidos, porVencer };
  }, [contracts, expiring]);

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return contracts
      .filter((c) => {
        const name = (c.employee_name || '').toLowerCase();
        const doc = (c.document || '').toLowerCase();
        return name.includes(t) || doc.includes(t);
      })
      .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0));
  }, [contracts, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const submitNew = async () => {
    if (!newForm.employeeId || !newForm.startDate) {
      toast({ title: 'Empleado y fecha de inicio son obligatorios', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    try {
      await createContract({
        employeeId: Number(newForm.employeeId),
        contractType: newForm.contractType,
        startDate: newForm.startDate,
        endDate: newForm.endDate || undefined,
        position: newForm.position || undefined,
        department: newForm.department || undefined,
        weeklyHours: newForm.weeklyHours === '' ? 40 : Number(newForm.weeklyHours),
        salaryAgreed: newForm.salary === '' ? undefined : Number(newForm.salary),
        sectionName: newForm.sectionName || undefined,
        categoryName: newForm.categoryName || undefined,
        referenceName: newForm.referenceName || undefined,
        hours: newForm.hours === '' ? undefined : Number(newForm.hours),
        cediiapRate: newForm.cediiapRate === '' ? undefined : Number(newForm.cediiapRate),
        cediiapPayMode: newForm.cediiapPayMode,
        laudoRate: newForm.laudoRate === '' ? undefined : Number(newForm.laudoRate),
        laudoPayMode: newForm.laudoPayMode,
        laudoStartDate: newForm.laudoStartDate || undefined,
        notes: newForm.notes || undefined,
        createdBy: actorUserId(),
      });
      toast({ title: 'Contrato creado', status: 'success', duration: 3000, isClosable: true });
      newModal.onClose();
      setNewForm({ ...EMPTY_CONTRACT_FORM, employeeId: employeeIdFilter });
      loadData();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo crear', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const openRenew = (row) => {
    setRenewForm({
      id: row.id,
      newEndDate: '',
      salaryAgreed: row.salary_agreed != null ? String(row.salary_agreed) : '',
    });
    renewModal.onOpen();
  };

  const submitRenew = async () => {
    if (!renewForm.newEndDate) {
      toast({ title: 'Indique la nueva fecha de fin', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    try {
      await renewContract(renewForm.id, {
        newEndDate: renewForm.newEndDate,
        salaryAgreed: renewForm.salaryAgreed === '' ? undefined : Number(renewForm.salaryAgreed),
        updatedBy: actorUserId(),
      });
      toast({ title: 'Contrato renovado', status: 'success', duration: 3000, isClosable: true });
      renewModal.onClose();
      loadData();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo renovar', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const openYear = (row) => {
    setYearForm({
      previousId: row.id,
      employeeId: row.employee_id,
      contractType: row.contract_type || 'FIXED_TERM',
      startDate: '',
      endDate: '',
      position: row.position || '',
      department: row.department || '',
      sectionName: row.section_name || 'Docencia',
      categoryName: row.category_name || '',
      referenceName: row.reference_name || '',
      hours: row.hours ?? '',
      cediiapRate: row.cediiap_rate ?? '',
      cediiapPayMode: row.cediiap_pay_mode || 'HOURLY',
      laudoRate: row.laudo_rate ?? '',
      laudoPayMode: row.laudo_pay_mode || 'HOURLY',
      laudoStartDate: row.laudo_start_date ? String(row.laudo_start_date).slice(0, 10) : '',
    });
  };

  const submitYear = async () => {
    if (!yearForm?.startDate || yearForm.cediiapRate === '' || yearForm.laudoRate === '') {
      toast({
        title: 'Completá la fecha de inicio y los dos valores hora',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      await createContract({
        employeeId: Number(yearForm.employeeId),
        contractType: yearForm.contractType,
        startDate: yearForm.startDate,
        endDate: yearForm.endDate || undefined,
        position: yearForm.position || undefined,
        department: yearForm.department || undefined,
        sectionName: yearForm.sectionName || undefined,
        categoryName: yearForm.categoryName || undefined,
        referenceName: yearForm.referenceName || undefined,
        hours: yearForm.hours === '' ? undefined : Number(yearForm.hours),
        cediiapRate: Number(yearForm.cediiapRate),
        cediiapPayMode: yearForm.cediiapPayMode,
        laudoRate: Number(yearForm.laudoRate),
        laudoPayMode: yearForm.laudoPayMode,
        laudoStartDate: yearForm.laudoStartDate || undefined,
        createdBy: actorUserId(),
      });
      await updateContract(yearForm.previousId, {
        status: 'RENEWED',
        endDate: dayBefore(yearForm.startDate) || undefined,
        updatedBy: actorUserId(),
      });
      toast({ title: 'Año cargado', status: 'success', duration: 3000, isClosable: true });
      setYearForm(null);
      loadData();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo cargar el año', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const confirmTerminate = async () => {
    if (!terminateId) return;
    try {
      await terminateContract(terminateId, { updatedBy: actorUserId() });
      toast({ title: 'Contrato terminado', status: 'success', duration: 3000, isClosable: true });
      terminateDialog.onClose();
      setTerminateId(null);
      loadData();
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo terminar', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const summaryCards = [
    {
      icon: MdCheckCircle,
      label: 'Activos',
      value: stats.activos,
      color: 'green.500',
      bg: useColorModeValue('green.50', 'green.900'),
    },
    {
      icon: MdWarning,
      label: 'Por vencer (30 días)',
      value: stats.porVencer,
      color: 'orange.500',
      bg: useColorModeValue('orange.50', 'orange.900'),
    },
    {
      icon: MdDescription,
      label: 'Vencidos',
      value: stats.vencidos,
      color: 'red.500',
      bg: useColorModeValue('red.50', 'red.900'),
    },
  ];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {expiring.length > 0 && (
        <Alert status="warning" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="700">Contratos próximos a vencer ({expiring.length})</Text>
            <Text fontSize="sm">
              {expiring.slice(0, 5).map((c) => c.employee_name || `ID ${c.employee_id}`).join(', ')}
              {expiring.length > 5 ? '…' : ''}
            </Text>
          </Box>
        </Alert>
      )}

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
        {summaryCards.map((c, i) => (
          <GridItem key={i}>
            <Card bg={c.bg} p={5}>
              <Flex align="center">
                <Icon as={c.icon} w={6} h={6} color={c.color} mr={3} />
                <Stat>
                  <StatLabel fontSize="xs" color="gray.500">
                    {c.label}
                  </StatLabel>
                  <StatNumber fontSize="lg" color={c.color}>
                    {c.value}
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    Contratos registrados
                  </StatHelpText>
                </Stat>
              </Flex>
            </Card>
          </GridItem>
        ))}
      </Grid>

      <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        <Flex px="25px" justify="space-between" mb="20px" align="center" flexWrap="wrap" gap={4}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            {employeeIdFilter ? 'Historial de contratos' : 'Contratos de empleados'}
          </Text>
          <Button size="sm" leftIcon={<MdAdd />} colorScheme="brand" onClick={newModal.onOpen}>
            Nuevo Contrato
          </Button>
        </Flex>

        <Flex px="25px" mb="20px" gap={4} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por empleado o CI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Select
            maxW="200px"
            placeholder="Todos los estados"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ACTIVE">Activo</option>
            <option value="EXPIRED">Vencido</option>
            <option value="TERMINATED">Terminado</option>
            <option value="RENEWED">Renovado</option>
          </Select>
        </Flex>

        {!loading && (
          <>
            <Box overflowX="auto">
              <Table variant="simple" color="gray.500" mb="12px" size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor} fontSize="xs">
                      Empleado
                    </Th>
                    <Th borderColor={borderColor} fontSize="xs">Contrato</Th>
                    <Th borderColor={borderColor} fontSize="xs">Sección</Th>
                    <Th borderColor={borderColor} fontSize="xs">Categoría</Th>
                    <Th borderColor={borderColor} fontSize="xs">Referencia</Th>
                    <Th borderColor={borderColor} fontSize="xs">Inicio</Th>
                    <Th borderColor={borderColor} fontSize="xs">Fin</Th>
                    <Th borderColor={borderColor} fontSize="xs">Cobro</Th>
                    <Th borderColor={borderColor} fontSize="xs">Tipo</Th>
                    <Th borderColor={borderColor} fontSize="xs">Dedicación</Th>
                    <Th borderColor={borderColor} fontSize="xs">Nominal hora CEDIIAP</Th>
                    <Th borderColor={borderColor} fontSize="xs">Nominal CEDIIAP</Th>
                    <Th borderColor={borderColor} fontSize="xs">Nominal hora laudo</Th>
                    <Th borderColor={borderColor} fontSize="xs">Nominal laudo</Th>
                    <Th borderColor={borderColor} fontSize="xs">Antigüedad</Th>
                    <Th borderColor={borderColor} fontSize="xs">Estado</Th>
                    <Th borderColor={borderColor} fontSize="xs">
                      Acciones
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginated.map((row) => {
                    const st = CONTRACT_STATUS[row.status] || { label: row.status, color: 'gray' };
                    const ct = row.contract_type || row.contractType;
                    const typeLabel = CONTRACT_TYPE_LABEL[ct] || ct;
                    const scheme = TYPE_BADGE_SCHEME[ct] || 'gray';
                    return (
                      <Tr key={row.id} _hover={{ bg: rowHover }}>
                        <Td borderColor={borderColor}>
                          <Text fontWeight="600" fontSize="sm" color={textColor}>
                            {row.employee_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {row.document}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.id}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.section_name || '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.category_name || '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.reference_name || '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{formatDate(row.start_date)}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{formatDate(row.end_date)}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.cediiap_pay_mode ? payModeLabel(row.cediiap_pay_mode) : '—'}</Text></Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme={scheme} fontSize="xs" borderRadius="6px" px={2}>{typeLabel}</Badge>
                        </Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.hours ?? '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.cediiap_rate != null ? money.format(Number(row.cediiap_rate)) : '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{money.format(nominalTotal(row.cediiap_rate, row.hours, row.cediiap_pay_mode))}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{row.laudo_rate != null ? money.format(Number(row.laudo_rate)) : '—'}</Text></Td>
                        <Td borderColor={borderColor}><Text fontSize="sm">{money.format(nominalTotal(row.laudo_rate, row.hours, row.laudo_pay_mode))}</Text></Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm" fontWeight="700">{row.seniority_percent != null ? `${row.seniority_percent}%` : '—'}</Text>
                          <Text fontSize="xs" color="gray.500">{money.format(Number(row.seniority || 0))}</Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme={st.color} fontSize="xs" borderRadius="6px" px={2}>{st.label}</Badge>
                        </Td>
                        <Td borderColor={borderColor}>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="teal"
                              isDisabled={row.status !== 'ACTIVE'}
                              onClick={() => openYear(row)}
                            >
                              Año
                            </Button>
                            <IconButton
                              size="xs"
                              icon={<MdAutorenew />}
                              colorScheme="blue"
                              variant="ghost"
                              aria-label="Renovar"
                              isDisabled={row.status === 'TERMINATED'}
                              onClick={() => openRenew(row)}
                            />
                            <IconButton
                              size="xs"
                              icon={<MdBlock />}
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Terminar"
                              isDisabled={row.status !== 'ACTIVE'}
                              onClick={() => {
                                setTerminateId(row.id);
                                terminateDialog.onOpen();
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            <Flex px="25px" pb={4} justify="space-between" align="center">
              <Text fontSize="xs" color="gray.500">
                Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} de {filtered.length}
              </Text>
              <HStack spacing={2}>
                <IconButton
                  size="sm"
                  icon={<MdChevronLeft />}
                  isDisabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  variant="outline"
                  aria-label="Anterior"
                />
                <Text fontSize="sm" fontWeight="600">
                  {page + 1} / {totalPages}
                </Text>
                <IconButton
                  size="sm"
                  icon={<MdChevronRight />}
                  isDisabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                  variant="outline"
                  aria-label="Siguiente"
                />
              </HStack>
            </Flex>
          </>
        )}

        {!loading && filtered.length === 0 && (
          <Flex justify="center" align="center" py={10} direction="column">
            <Icon as={MdDescription} w={12} h={12} color="gray.300" mb={3} />
            <Text color="gray.500" fontSize="md">
              {searchTerm || statusFilter ? 'No hay contratos con esos filtros' : 'No hay contratos registrados'}
            </Text>
          </Flex>
        )}
      </Card>

      <Modal isOpen={newModal.isOpen} onClose={newModal.onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Nuevo contrato</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Empleado</FormLabel>
                <Select
                  placeholder="Seleccionar"
                  value={newForm.employeeId}
                  onChange={(e) => setNewForm((f) => ({ ...f, employeeId: e.target.value }))}
                >
                  {employees.map((emp) => {
                    const p = emp.person || {};
                    const ln = p.lastName || p.last_name || '';
                    return (
                      <option key={emp.id} value={emp.id}>
                        {p.name} {ln} — {p.document}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Tipo</FormLabel>
                <Select value={newForm.contractType} onChange={(e) => setNewForm((f) => ({ ...f, contractType: e.target.value }))}>
                  {Object.entries(CONTRACT_TYPE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Fecha inicio</FormLabel>
                <Input type="date" value={newForm.startDate} onChange={(e) => setNewForm((f) => ({ ...f, startDate: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Fecha fin</FormLabel>
                <Input type="date" value={newForm.endDate} onChange={(e) => setNewForm((f) => ({ ...f, endDate: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Cargo</FormLabel>
                <Input value={newForm.position} onChange={(e) => setNewForm((f) => ({ ...f, position: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Departamento</FormLabel>
                <Input value={newForm.department} onChange={(e) => setNewForm((f) => ({ ...f, department: e.target.value }))} />
              </FormControl>
              <HStack>
                <FormControl>
                  <FormLabel>Sección</FormLabel>
                  <Input value={newForm.sectionName} onChange={(e) => setNewForm((f) => ({ ...f, sectionName: e.target.value }))} placeholder="Docencia" />
                </FormControl>
                <FormControl>
                  <FormLabel>Categoría</FormLabel>
                  <Input value={newForm.categoryName} onChange={(e) => setNewForm((f) => ({ ...f, categoryName: e.target.value }))} placeholder="Grado 2" />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Asignatura o referencia</FormLabel>
                <Input
                  placeholder="Ej. Lic. en Psicopedagogía I y II"
                  value={newForm.referenceName}
                  onChange={(e) => setNewForm((f) => ({ ...f, referenceName: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Cantidad de horas</FormLabel>
                <Input type="number" value={newForm.hours} onChange={(e) => setNewForm((f) => ({ ...f, hours: e.target.value }))} />
              </FormControl>
              <Text fontSize="sm" fontWeight="700">Valor fijado por CEDIIAP</Text>
              <HStack>
                <FormControl>
                  <FormLabel>Valor</FormLabel>
                  <Input type="number" value={newForm.cediiapRate} onChange={(e) => setNewForm((f) => ({ ...f, cediiapRate: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Modalidad</FormLabel>
                  <Select value={newForm.cediiapPayMode} onChange={(e) => setNewForm((f) => ({ ...f, cediiapPayMode: e.target.value }))}>
                    <option value="HOURLY">Por hora</option>
                    <option value="MONTHLY">Mensual</option>
                  </Select>
                </FormControl>
              </HStack>
              <Text fontSize="sm" fontWeight="700">Valor del laudo (antigüedad 2% por año, tope 20%)</Text>
              <HStack>
                <FormControl>
                  <FormLabel>Valor</FormLabel>
                  <Input type="number" value={newForm.laudoRate} onChange={(e) => setNewForm((f) => ({ ...f, laudoRate: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Modalidad</FormLabel>
                  <Select value={newForm.laudoPayMode} onChange={(e) => setNewForm((f) => ({ ...f, laudoPayMode: e.target.value }))}>
                    <option value="HOURLY">Por hora</option>
                    <option value="MONTHLY">Mensual</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Inicio de antigüedad (fecha del laudo)</FormLabel>
                <Input type="date" value={newForm.laudoStartDate} onChange={(e) => setNewForm((f) => ({ ...f, laudoStartDate: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Horas semanales</FormLabel>
                <Input
                  type="number"
                  value={newForm.weeklyHours}
                  onChange={(e) => setNewForm((f) => ({ ...f, weeklyHours: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Salario acordado</FormLabel>
                <Input type="number" value={newForm.salary} onChange={(e) => setNewForm((f) => ({ ...f, salary: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Notas</FormLabel>
                <Textarea value={newForm.notes} onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))} rows={3} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={newModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="brand" onClick={submitNew}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!yearForm} onClose={() => setYearForm(null)} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cargar año</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Se mantienen la asignatura, las horas y la fecha de antigüedad del laudo. Cargá las dos fechas del período y los dos valores hora.
              </Text>
              <Text fontSize="sm"><strong>Referencia:</strong> {yearForm?.referenceName || '—'}</Text>
              <Text fontSize="sm"><strong>Antigüedad desde:</strong> {formatDate(yearForm?.laudoStartDate)}</Text>
              <FormControl isRequired>
                <FormLabel>Inicio del período (valor CEDIIAP)</FormLabel>
                <Input type="date" value={yearForm?.startDate || ''} onChange={(e) => setYearForm((f) => ({ ...f, startDate: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Fin del período</FormLabel>
                <Input type="date" value={yearForm?.endDate || ''} onChange={(e) => setYearForm((f) => ({ ...f, endDate: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Valor hora CEDIIAP</FormLabel>
                <Input type="number" value={yearForm?.cediiapRate ?? ''} onChange={(e) => setYearForm((f) => ({ ...f, cediiapRate: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Valor hora laudo</FormLabel>
                <Input type="number" value={yearForm?.laudoRate ?? ''} onChange={(e) => setYearForm((f) => ({ ...f, laudoRate: e.target.value }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setYearForm(null)}>Cancelar</Button>
            <Button colorScheme="teal" onClick={submitYear}>Guardar año</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={renewModal.isOpen} onClose={renewModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Renovar contrato</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4} isRequired>
              <FormLabel>Nueva fecha de fin</FormLabel>
              <Input
                type="date"
                value={renewForm.newEndDate}
                onChange={(e) => setRenewForm((f) => ({ ...f, newEndDate: e.target.value }))}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Salario acordado</FormLabel>
              <Input
                type="number"
                value={renewForm.salaryAgreed}
                onChange={(e) => setRenewForm((f) => ({ ...f, salaryAgreed: e.target.value }))}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={renewModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={submitRenew}>
              Renovar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={terminateDialog.isOpen} leastDestructiveRef={cancelRef} onClose={terminateDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Terminar contrato
            </AlertDialogHeader>
            <AlertDialogBody>¿Confirma dar por terminado este contrato?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={terminateDialog.onClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={confirmTerminate} ml={3}>
                Terminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
